import React, { useEffect, useState } from "react";
import "../styles/ApplicationDetailDrawer.css";
import Skeleton from "../components/common/Skeleton";
import FadeIn from "../components/common/FadeIn";
import { tEnum } from "../../config/translations";
import api from "../../shared/AxiosConfig";

const ApplicationDetailDrawer = ({ open, onClose, data, loading }) => {

    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [actionNote, setActionNote] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    const detail = data;

    useEffect(() => {
        if (open) {
            setMounted(true);
            setActionNote("");
            requestAnimationFrame(() =>
                requestAnimationFrame(() => setVisible(true))
            );
        } else {
            setVisible(false);
            setTimeout(() => setMounted(false), 380);
        }
    }, [open]);

    if (!mounted) return null;

    const formatDate = (d, withTime = true) =>
        d
            ? new Date(d).toLocaleString(
                "tr-TR",
                withTime
                    ? {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                    }
                    : {
                        day: "2-digit",
                        month: "long",
                        year: "numeric"
                    }
            )
            : "-";

    const statusColor = {
        NEW: "badge-new",
        REVIEWING: "badge-review",
        APPROVED: "badge-approve",
        REJECTED: "badge-rejected",
        WITHDRAWN: "badge-withdrawn"
    };

    const personal = detail?.personal;
    const identityKind =
        personal?.identityDocumentType ||
        (personal?.passportNumber && !personal?.tckn ? "PASSPORT" : "TCKN");
    const identityNo =
        identityKind === "PASSPORT"
            ? personal?.passportNumber || "-"
            : personal?.tckn || "-";

    // ================== ACTION ==================
    const handleDecision = async (action) => {
        if (!detail?.publicId) return;

        setActionLoading(true);
        try {
            await api.patch(`/application/${detail.publicId}/status`, {
                action,
                note: actionNote?.trim() || undefined
            });

            setActionNote("");
            onClose(true);
        } catch (e) {
            console.error(e);
        } finally {
            setActionLoading(false);
        }
    };


    return (
        <>
            <div
                className={`drawer-overlay ${visible ? "show" : ""}`}
                onClick={onClose}
            />

            <div className={`detail-drawer ${visible ? "open" : ""}`}>
                <div className="drawer-header">
                    {loading ? (
                        <Skeleton width="60%" height={22}/>
                    ) : (
                        <h3>Başvuru Detayı</h3>
                    )}
                    <button className="drawer-close" onClick={onClose}>×</button>
                </div>

                <div className="drawer-body">

                    {/* STATUS */}
                    <FadeIn delay={0.05}>
                        <div className="drawer-section">
                            <div className="section-title">Başvuru Durumu</div>

                            {loading ? (
                                <Skeleton height={50} className="mb-2"/>
                            ) : (
                                <>
                                    <span className={`status-badge ${statusColor[detail?.status?.value]}`}>
                                        {tEnum("STATUS", detail?.status?.value)}
                                    </span>

                                    {/* ---- NOT (OPSIYONEL) ---- */}
                                    {detail?.status?.value !== "REJECTED" &&
                                        detail?.status?.value !== "WITHDRAWN" && (
                                        <div className="action-note-wrap">
                                            <label className="action-note-label">Not (opsiyonel)</label>
                                            <textarea
                                                className="drawer-input action-note-input"
                                                placeholder="Kabul, red veya vazgeçme notu..."
                                                value={actionNote}
                                                onChange={(e) => setActionNote(e.target.value)}
                                                rows={2}
                                            />
                                        </div>
                                    )}

                                    {/* ---- ACTION BUTTONS ---- */}
                                    {detail?.status?.value !== "REJECTED" &&
                                        detail?.status?.value !== "WITHDRAWN" && (
                                        <div className="action-buttons">

                                            {/* NEW */}
                                            {detail?.status?.value === "NEW" && (
                                                <>
                                                    <button
                                                        className="btn btn-warning"
                                                        onClick={() => handleDecision("REVIEWING")}
                                                        disabled={actionLoading}
                                                    >
                                                        İncelemeye Al
                                                    </button>

                                                    <button
                                                        className="btn btn-danger"
                                                        onClick={() => handleDecision("REJECTED")}
                                                        disabled={actionLoading}
                                                    >
                                                        Reddet
                                                    </button>
                                                </>
                                            )}

                                            {/* REVIEWING */}
                                            {detail?.status?.value === "REVIEWING" && (
                                                <>
                                                    <button
                                                        className="btn btn-success"
                                                        onClick={() => handleDecision("APPROVED")}
                                                        disabled={actionLoading}
                                                    >
                                                        Onayla
                                                    </button>

                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={() => handleDecision("WITHDRAWN")}
                                                        disabled={actionLoading}
                                                    >
                                                        Vazgeç
                                                    </button>

                                                    <button
                                                        className="btn btn-danger"
                                                        onClick={() => handleDecision("REJECTED")}
                                                        disabled={actionLoading}
                                                    >
                                                        Reddet
                                                    </button>
                                                </>
                                            )}

                                            {/* APPROVED */}
                                            {detail?.status?.value === "APPROVED" && (
                                                <>
                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={() => handleDecision("WITHDRAWN")}
                                                        disabled={actionLoading}
                                                    >
                                                        Vazgeç
                                                    </button>

                                                    <button
                                                        className="btn btn-danger"
                                                        onClick={() => handleDecision("REJECTED")}
                                                        disabled={actionLoading}
                                                    >
                                                        Reddet
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </FadeIn>

                    {/* TIMESTAMPS & NOTES */}
                    <FadeIn delay={0.3}>
                    <div className="drawer-section">
                            <div className="section-title">Tarihçe</div>

                            {loading ? (
                                <Skeleton height={50} className="mb-2"/>
                            ) : (
                                <div className="info-grid single-column">
                                    <div><strong>Başvuru Tarihi:</strong> {formatDate(detail?.createdAt || detail?.timestamps?.createdAt)}</div>
                                    <div><strong>Son Güncelleme:</strong> {formatDate(detail?.status?.statusUpdatedAt)}</div>

                                    {detail?.status?.value === "APPROVED" && detail?.approvedNote && (
                                        <div className="mt-2">
                                            <strong>Kabul Notu:</strong>
                                            <p style={{ marginTop: 4, marginBottom: 0 }}>{detail.approvedNote}</p>
                                        </div>
                                    )}
                                    {detail?.status?.value === "REJECTED" && detail?.rejectedNote && (
                                        <div className="mt-2">
                                            <strong>Red Notu:</strong>
                                            <p style={{ marginTop: 4, marginBottom: 0 }}>{detail.rejectedNote}</p>
                                        </div>
                                    )}
                                    {detail?.status?.value === "WITHDRAWN" && detail?.withdrawnNote && (
                                        <div className="mt-2">
                                            <strong>Vazgeçme Notu:</strong>
                                            <p style={{ marginTop: 4, marginBottom: 0 }}>{detail.withdrawnNote}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </FadeIn>

                    {/* PERSONAL */}
                    <FadeIn delay={0.1}>
                        <div className="drawer-section">
                            <div className="section-title">Kişisel Bilgiler</div>

                            {loading ? (
                                <Skeleton height={50} className="mb-2"/>
                            ) : (
                                <div className="info-grid">
                                    <div><strong>Başvuru Kanalı:</strong> {tEnum("APPLICATION_CHANNEL", detail?.applicationChannel)}</div>
                                    <div><strong>Ad
                                        Soyad:</strong> {detail?.personal?.firstName} {detail?.personal?.lastName}</div>
                                    <div>
                                        <strong>Kimlik:</strong>{" "}
                                        {tEnum("IDENTITY_DOCUMENT_TYPE", identityKind)} {identityNo}
                                    </div>
                                    <div><strong>Doğum Tarihi:</strong> {formatDate(detail?.personal?.birthDate, false)}
                                    </div>
                                    {/*<div><strong>Doğum Yeri:</strong> {detail?.personal?.birthPlace}</div>*/}
                                    <div><strong>Cinsiyet:</strong> {tEnum("GENDER", detail?.personal?.gender)}</div>
                                </div>
                            )}
                        </div>
                    </FadeIn>

                    {/* CONTACT */}
                    <FadeIn delay={0.15}>
                        <div className="drawer-section">
                            <div className="section-title">İletişim</div>

                            {loading ? (
                                <Skeleton height={50} className="mb-2"/>
                            ) : (
                                <div className="info-grid single-column">
                                    <div><strong>Telefon:</strong> {detail?.contact?.phone}</div>
                                    <div><strong>Şehir:</strong> {detail?.contact?.city}</div>
                                    <div><strong>Email:</strong> {detail?.contact?.email}</div>
                                    <div><strong>Adres:</strong> {detail?.contact?.address}</div>
                                </div>
                            )}
                        </div>
                    </FadeIn>

                    {/* EDUCATION */}
                    <FadeIn delay={0.2}>
                        <div className="drawer-section">
                            <div className="section-title">Eğitim & İlgi</div>

                            {loading ? (
                                <Skeleton height={50} className="mb-2"/>
                            ) : (
                                <div className="info-grid single-column">
                                    <div><strong>Meslek:</strong> {detail?.education?.profession}</div>
                                    <div><strong>Okul:</strong> {detail?.education?.lastGraduatedSchool}</div>
                                    <div>
                                        <strong>Spor Deneyimi:</strong>{" "}
                                        {detail?.education?.hasSportsExperience ? "Var" : "Yok"}
                                    </div>

                                    <div>
                                        <strong>Futbol Eğitimi:</strong>{" "}
                                        {detail?.education?.hasPreviousFootballEducation ? "Var" : "Yok"}
                                    </div>
                                </div>
                            )}
                        </div>
                    </FadeIn>

                    {/* MOTIVATION */}
                    <FadeIn delay={0.25}>
                        <div className="drawer-section">
                        <div className="section-title">Motivasyon</div>

                            {loading ? (
                                <Skeleton height={50} className="mb-2"/>
                            ) : (
                                <div className="motivation-box">
                                    <p><strong>Hayattaki Amacı Nedir?</strong><br/>
                                        {detail?.motivation?.lifeGoal}
                                    </p>
                                    <p><strong>Neden Katılmak İstiyor?</strong><br/>
                                        {detail?.motivation?.participationMotivation}
                                    </p>
                                    <p><strong>Neden Footex?</strong><br/>
                                        {detail?.motivation?.whyFootex}
                                    </p>
                                    <p><strong>Futbol Anlamı?</strong><br/>
                                        {detail?.motivation?.footballMeaning}
                                    </p>
                                </div>
                            )}
                        </div>
                    </FadeIn>

                    {/* VIDEO */}
                    <FadeIn delay={0.35}>
                        <div className="drawer-section">
                            <div className="section-title">Tanıtım Videosu</div>

                            {loading ? (
                                <Skeleton height={220} />
                            ) : detail?.video?.url ? (
                                <>
                                    <div className="video-wrapper">
                                        <video
                                            src={detail.video.url}
                                            controls
                                            preload="metadata"
                                            controlsList="nodownload"
                                        />
                                    </div>

                                    <div className="video-actions">
                                        <a
                                            href={detail.video.url}
                                            download
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-secondary btn-sm"
                                        >
                                            Videoyu İndir
                                        </a>

                                        <span className="video-size">
            {(detail.video.size / 1024 / 1024).toFixed(1)} MB
          </span>
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-gray-500">
                                    Video yüklenmemiş
                                </p>
                            )}
                        </div>
                    </FadeIn>

                 

                </div>
            </div>
        </>
    );
};

export default ApplicationDetailDrawer;
